// IndependentContractorAgreement.js
// Shown to new providers after onboarding is complete, before first dashboard access.
// Provider must scroll through the agreement, check the box, and click agree.
// Writes contractorAgreementSigned: true and contractorAgreementSignedAt to Firestore.
// ProviderDashboard redirects here if the flag is missing or false.

import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function IndependentContractorAgreement() {
  const navigate = useNavigate();

  // Whether the provider has checked the agreement checkbox
  const [agreed, setAgreed] = useState(false);

  // Tracks submission state so we can disable the button while writing to Firestore
  const [submitting, setSubmitting] = useState(false);

  // Error message if the Firestore write fails
  const [error, setError] = useState('');

  // Page fade-in on mount
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setTimeout(() => setVisible(true), 50);
  }, []);

  // Called when the provider clicks "I Agree & Continue to Dashboard"
  const handleAgree = async () => {
    if (!agreed || submitting) return;

    setSubmitting(true);
    setError('');

    const user = auth.currentUser;

    // Guard — should never happen since ProtectedRoute already checks auth
    if (!user) {
      setError('Session expired. Please log in again.');
      setSubmitting(false);
      return;
    }

    try {
      // Write the signed flag and ISO timestamp to the provider's Firestore document
      await updateDoc(doc(db, 'users', user.uid), {
        contractorAgreementSigned: true,
        contractorAgreementSignedAt: new Date().toISOString(),
      });

      // Agreement saved — go to handbook acknowledgment (next step before dashboard)
      navigate('/provider/handbook');
    } catch (err) {
      console.error('Failed to save agreement:', err);
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="agreement-page"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
    >
      {/* ===== CARD ===== */}
      <div className="agreement-card">

        {/* Header */}
        <div className="agreement-header">
          <div className="agreement-logo">Evermark Memorial</div>
          <h1 className="agreement-title">Independent Contractor Agreement</h1>
          <p className="agreement-subtitle">
            Please read the full agreement below before continuing to your dashboard.
          </p>
        </div>

        {/* ===== SCROLLABLE AGREEMENT TEXT ===== */}
        <div className="agreement-scroll-box">

          <p className="agreement-intro">
            By checking the box below and clicking "I Agree", you confirm that you have read,
            understood, and agree to the following terms:
          </p>

          {/* Section 1 */}
          <h3 className="agreement-section-title">1. Independent Contractor Status</h3>
          <p className="agreement-section-body">
            You are an independent contractor, not an employee of Evermark Memorial. You are
            responsible for your own taxes, insurance, and business expenses. Evermark Memorial
            does not withhold taxes on your behalf.
          </p>

          {/* Section 2 */}
          <h3 className="agreement-section-title">2. Transportation and Tools</h3>
          <p className="agreement-section-body">
            You provide your own transportation, tools, and supplies needed to complete jobs.
            You are responsible for maintaining your own vehicle insurance.
          </p>

          {/* Section 3 */}
          <h3 className="agreement-section-title">3. Voluntary Work</h3>
          <p className="agreement-section-body">
            You choose which jobs to accept. No guaranteed work or minimum hours are promised.
            You may decline any job at any time before claiming it.
          </p>

          {/* Section 4 */}
          <h3 className="agreement-section-title">4. Cemetery Rules and Compliance</h3>
          <p className="agreement-section-body">
            You must follow all rules and regulations of every cemetery you visit, including:
          </p>
          <ul className="agreement-list">
            <li>No prohibited chemicals or tools</li>
            <li>No disrespectful conduct</li>
            <li>No entering closed or restricted areas</li>
            <li>No interference with funerals or active services</li>
            <li>No unauthorized decorations</li>
            <li>Stop work immediately if asked by cemetery staff</li>
          </ul>

          {/* Section 5 */}
          <h3 className="agreement-section-title">5. Approved Cleaning Practices</h3>
          <p className="agreement-section-body">
            You may only use gentle, stone-safe cleaning methods. The following are strictly
            prohibited:
          </p>
          <ul className="agreement-list">
            <li>Bleach or harsh chemicals</li>
            <li>Wire brushes or abrasive tools</li>
            <li>Pressure washers on aged or fragile stones</li>
            <li>Any attempt to move, lift, reset, repair, or physically alter memorial stones</li>
          </ul>
          <p className="agreement-section-body">
            Evermark Memorial provides general cleaning and maintenance only. We do not perform
            repairs or restoration.
          </p>

          {/* Section 6 */}
          <h3 className="agreement-section-title">6. Pre-Job Inspection</h3>
          <p className="agreement-section-body">
            Before beginning any job you must photograph the memorial and surrounding area to
            document its existing condition. This protects you and the customer from damage
            disputes.
          </p>

          {/* Section 7 */}
          <h3 className="agreement-section-title">7. Damage Limitation</h3>
          <p className="agreement-section-body">
            Many memorials contain hidden structural weaknesses due to age, weather exposure,
            prior repairs, or material deterioration. You accept that Evermark Memorial is not
            liable for damage that occurs as a result of pre-existing conditions, natural
            deterioration, fragile or aged stones, weathered engravings, unstable bases, or old
            adhesives and repairs.
          </p>

          {/* Section 8 */}
          <h3 className="agreement-section-title">8. Photo Documentation Standards</h3>
          <p className="agreement-section-body">
            You must submit clear before and after photos for every job. Photos must accurately
            represent the work completed. Photos may be used by Evermark Memorial for proof of
            completion, dispute resolution, marketing, and social media unless the customer has
            opted out.
          </p>

          {/* Section 9 */}
          <h3 className="agreement-section-title">9. Confidentiality</h3>
          <p className="agreement-section-body">
            You agree to keep customer information, grave locations, and personal details
            confidential. You may not share or publish customer information without consent.
          </p>

          {/* Section 10 */}
          <h3 className="agreement-section-title">10. Representation</h3>
          <p className="agreement-section-body">
            You may not represent yourself as an owner, manager, or employee of Evermark
            Memorial. You are a service provider on the Evermark Memorial platform.
          </p>

          {/* Section 11 */}
          <h3 className="agreement-section-title">11. Subcontracting</h3>
          <p className="agreement-section-body">
            You may not subcontract or assign jobs to another person without written permission
            from Evermark Memorial.
          </p>

          {/* Section 12 */}
          <h3 className="agreement-section-title">12. Grounds for Removal</h3>
          <p className="agreement-section-body">
            Evermark Memorial may remove you from the platform at any time for:
          </p>
          <ul className="agreement-list">
            <li>Violation of cemetery rules</li>
            <li>Fraudulent photo submissions</li>
            <li>Customer complaints</li>
            <li>Unsafe or disrespectful conduct</li>
            <li>Violation of any term in this agreement</li>
          </ul>

          {/* Section 13 */}
          <h3 className="agreement-section-title">13. Right to Refuse</h3>
          <p className="agreement-section-body">
            Evermark Memorial reserves the right to refuse service to any caretaker for unsafe
            requests, rule violations, or conduct violations.
          </p>

          {/* Section 14 */}
          <h3 className="agreement-section-title">14. Tax Responsibility</h3>
          <p className="agreement-section-body">
            You are responsible for reporting and paying all applicable taxes on income earned
            through the Evermark Memorial platform. You will receive a 1099 form if you earn
            $600 or more in a calendar year.
          </p>

          {/* Section 15 */}
          <h3 className="agreement-section-title">15. Risk Acknowledgment</h3>
          <p className="agreement-section-body">
            You acknowledge that grave care involves outdoor and manual work. You accept the
            physical risks associated with this work including weather conditions, uneven
            terrain, and physical exertion.
          </p>

        </div>
        {/* End scrollable box */}

        {/* ===== CHECKBOX ROW ===== */}
        <label className="agreement-checkbox-label">
          <input
            type="checkbox"
            className="agreement-checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span className="agreement-checkbox-text">
            I have read and agree to the Independent Contractor Agreement
          </span>
        </label>

        {/* Error message shown if Firestore write fails */}
        {error && <p className="agreement-error">{error}</p>}

        {/* ===== AGREE BUTTON ===== */}
        {/* Disabled until checkbox is checked and not mid-submission */}
        <button
          className={`agreement-btn${agreed && !submitting ? '' : ' agreement-btn--disabled'}`}
          onClick={handleAgree}
          disabled={!agreed || submitting}
        >
          {submitting ? 'Saving...' : 'I Agree & Continue to Dashboard'}
        </button>

      </div>
      {/* End card */}
    </div>
  );
}

export default IndependentContractorAgreement;
